// src/utils/indexedDB.js
// Secure token storage using IndexedDB

const DB_NAME = 'CARO_Portal_Auth';
const DB_VERSION = 1;
const STORE_NAME = 'auth_tokens';

/**
 * Open IndexedDB database
 * @returns {Promise<IDBDatabase>}
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error('Failed to open IndexedDB'));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Store a value in IndexedDB
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {Promise<void>}
 */
export const setItem = async (key, value) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const data = {
      key,
      value,
      timestamp: new Date().getTime()
    };

    return new Promise((resolve, reject) => {
      const request = store.put(data);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error(`Failed to store ${key}`));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB setItem error:', error);
    throw error;
  }
};

/**
 * Retrieve a value from IndexedDB
 * @param {string} key - Storage key
 * @returns {Promise<any>}
 */
export const getItem = async (key) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.get(key);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };

      request.onerror = () => {
        reject(new Error(`Failed to retrieve ${key}`));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB getItem error:', error);
    return null;
  }
};

/**
 * Remove a value from IndexedDB
 * @param {string} key - Storage key
 * @returns {Promise<void>}
 */
export const removeItem = async (key) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error(`Failed to remove ${key}`));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB removeItem error:', error);
    throw error;
  }
};

/**
 * Clear all stored data
 * @returns {Promise<void>}
 */
export const clear = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.clear();

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Failed to clear storage'));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB clear error:', error);
    throw error;
  }
};

/**
 * Get all keys in storage
 * @returns {Promise<string[]>}
 */
export const getAllKeys = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    return new Promise((resolve, reject) => {
      const request = store.getAllKeys();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(new Error('Failed to get all keys'));
      };

      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB getAllKeys error:', error);
    return [];
  }
};

/**
 * Check if IndexedDB is available
 * @returns {boolean}
 */
export const isAvailable = () => {
  return typeof indexedDB !== 'undefined';
};

/**
 * Migrate data from localStorage to IndexedDB
 * @param {string[]} keys - Keys to migrate
 * @returns {Promise<void>}
 */
export const migrateFromLocalStorage = async (keys) => {
  if (!isAvailable()) {
    console.warn('IndexedDB not available, skipping migration');
    return;
  }

  for (const key of keys) {
    try {
      const value = localStorage.getItem(key);
      if (value) {
        // Try to parse as JSON, otherwise store as string
        let parsedValue;
        try {
          parsedValue = JSON.parse(value);
        } catch {
          parsedValue = value;
        }

        await setItem(key, parsedValue);
        //console.log(`Migrated ${key} from localStorage to IndexedDB`);
      }
    } catch (error) {
      console.error(`Failed to migrate ${key}:`, error);
    }
  }
};

// Export default object with all methods
export default {
  setItem,
  getItem,
  removeItem,
  clear,
  getAllKeys,
  isAvailable,
  migrateFromLocalStorage
};
